/**
 * Copyright Highway9 Networks Inc.
 *
 * In-flight dedupe for `configureFetch`: concurrent identical read-like requests share one `fetch`;
 * each caller gets a cloned `Response` so bodies stay independent.
 */

import { dedupePromise } from "./concurrentDedupe";

const inFlightByKey = new Map<string, Promise<Response>>();

function urlToString(url: RequestInfo | URL): string {
  return typeof url === "string" ? url : url instanceof URL ? url.href : url.toString();
}

function pathEndsWithQuery(urlString: string): boolean {
  const path = urlString.split("?")[0].replace(/\/+$/, "") || "/";
  return path.endsWith("/query");
}

function bodyFingerprintForDedupe(init: RequestInit | undefined): string | null {
  const body = init?.body;
  if (body == null) return "";
  if (typeof body === "string") return body;
  if (body instanceof URLSearchParams) return body.toString();
  return null;
}

/**
 * Dedupe key for GETs and POST `…/query` with string-keyable body; otherwise `null`.
 */
export function getInFlightDedupeKey(url: RequestInfo | URL, init: RequestInit | undefined): string | null {
  const method = (init?.method ?? "GET").toUpperCase();
  const urlString = urlToString(url);

  if (method === "GET") {
    return `GET:${urlString}`;
  }
  if (method === "POST" && pathEndsWithQuery(urlString)) {
    const fp = bodyFingerprintForDedupe(init);
    return fp === null ? null : `POST:${urlString}:${fp}`;
  }
  return null;
}

export async function fetchWithInFlightDedupe(
  key: string | null,
  doFetch: () => Promise<Response>
): Promise<Response> {
  const response = await dedupePromise(key, inFlightByKey, doFetch);
  return response.clone();
}
